import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Package, Truck, Search, Award, Clock, CheckCircle, Shield, Zap, Eye } from 'lucide-react';

const HowItWorksPage = () => {
  useEffect(() => {
    document.title = 'How It Works | NICE Grading';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Submit your cards in minutes, ship to our secure lab, and receive professionally graded slabs in as few as 11 days. Simple, transparent, collector-friendly.');
    }
    return () => {
      document.title = 'NICE Grading | Collectors First. Always NICE.';
      if (metaDescription) {
        metaDescription.setAttribute('content', 'The most collector friendly grading service in the hobby. Powering the revolution one slab at a time.');
      }
    };
  }, []);

  const steps = [
    {
      number: 1,
      icon: Package,
      title: "Start Your Submission",
      shortDescription: "Create an account and fill out our simple submission form with details about your cards.",
      fullDescription: "Getting started is quick and easy. Our streamlined submission process takes just minutes to complete. Enter the number of cards you're submitting and watch as our transparent pricing instantly calculates your costs—no hidden fees, ever. Optionally add specific card details to help our team, or let our experts research and identify your cards upon receipt. You're always in control of your submission."
    },
    {
      number: 2,
      icon: Truck,
      title: "Ship Your Cards",
      shortDescription: "Securely package and ship your cards to our grading lab using your preferred shipping carrier.",
      fullDescription: "After completing your submission, download your personalized packing slip and include it with your cards. Use any carrier you trust—USPS, UPS, FedEx, or another option. We recommend insuring your shipment for peace of mind. Ship to our secure grading lab where your cards will be logged, tracked, and handled with expert care from the moment they arrive."
    },
    {
      number: 3,
      icon: Search,
      title: "Professional Grading",
      shortDescription: "Our experts carefully evaluate each card using our Nivora Intelligence™ technology.",
      fullDescription: "Once your cards arrive, payment is processed and your cards move immediately into our grading queue. Our certified graders use proprietary Nivora Intelligence™ technology combined with decades of expertise to ensure consistent, accurate evaluations. Every card is assessed across multiple quality factors including centering, corners, edges, and surface condition. We deliver professional grading you can trust—fast."
    },
    {
      number: 4,
      icon: Award,
      title: "Receive Your Slabs",
      shortDescription: "Your graded cards are encased in museum-grade holders and shipped back to you.",
      fullDescription: "After grading, your cards are securely encapsulated in premium, crystal-clear holders designed to showcase and protect your collection. Each slab features your card's grade prominently displayed along with detailed certification information. We ship your graded cards back to your preferred address with full tracking, typically within 10-15 business days from receipt. Your collection, elevated."
    }
  ];

  const timelineSteps = [
    { day: "Day 1", description: "Cards received, payment processed" },
    { day: "Days 2-8", description: "Professional grading" },
    { day: "Days 9-10", description: "Encapsulation and quality control" },
    { day: "Day 11+", description: "Shipped back with tracking" }
  ];

  const trustSignals = [
    { icon: CheckCircle, text: "Transparent pricing with zero upcharges" },
    { icon: Zap, text: "Rapid turnaround times" },
    { icon: Shield, text: "Museum-grade holders" },
    { icon: Eye, text: "Expert graders with decades of experience" },
    { icon: Clock, text: "Track your submission every step of the way" }
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />
      
      <main className="flex-grow">
        {/* Hero */}
        <section className="bg-[#002554] py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              How It Works
            </h1>
            <p className="text-xl text-gray-200 max-w-2xl mx-auto">
              Get your cards professionally graded in four simple steps
            </p>
          </div>
        </section>

        {/* Steps */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-16">
              {steps.map((step, index) => {
                const StepIcon = step.icon;
                return (
                  <div key={step.number} className="flex flex-col md:flex-row items-start gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-[#003594] rounded-2xl flex items-center justify-center">
                        <StepIcon className="w-8 h-8 text-white" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-sm font-semibold text-[#003594] bg-[#003594]/10 px-3 py-1 rounded-full">
                          Step {step.number}
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">
                        {step.title}
                      </h3>
                      <p className="text-lg text-gray-700 font-medium mb-3">
                        {step.shortDescription}
                      </p>
                      <p className="text-gray-500 leading-relaxed">
                        {step.fullDescription}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Typical Turnaround: 10-15 Business Days
              </h2>
              <p className="text-lg text-gray-600">
                From the moment we receive your cards to when they're back in your hands
              </p>
            </div>
            
            <div className="relative">
              {/* Timeline line */}
              <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-[#003594]/20"></div>
              
              <div className="space-y-8">
                {timelineSteps.map((item, index) => (
                  <div key={index} className={`flex items-center gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                      <div className={`bg-white rounded-xl p-6 shadow-sm border border-gray-100 ${index % 2 === 0 ? 'md:mr-8' : 'md:ml-8'}`}>
                        <span className="text-sm font-bold text-[#003594] uppercase tracking-wide">{item.day}</span>
                        <p className="text-gray-700 mt-1">{item.description}</p>
                      </div>
                    </div>
                    <div className="hidden md:flex w-12 h-12 bg-[#003594] rounded-full items-center justify-center flex-shrink-0 z-10">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Trust Signals Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Why Collectors Trust NICE
              </h2>
              <p className="text-lg text-gray-600">
                We're committed to providing the best grading experience in the industry
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {trustSignals.map((signal, index) => {
                const SignalIcon = signal.icon;
                return (
                  <div 
                    key={index} 
                    className="flex items-center gap-4 bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <SignalIcon className="w-6 h-6 text-green-600" />
                    </div>
                    <p className="text-gray-900 font-medium">{signal.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ Preview Section */}
        <section className="py-20 bg-[#003594]/5">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Common Questions
              </h2>
            </div>
            
            <div className="space-y-4">
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <h4 className="font-semibold text-gray-900 mb-2">What types of cards do you grade?</h4>
                <p className="text-gray-600">We grade sports cards, trading card game cards (Pokémon, Magic, Yu-Gi-Oh!), and other collectible cards. Our grading services cover vintage and modern cards alike.</p>
              </div>
              
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <h4 className="font-semibold text-gray-900 mb-2">How should I package my cards for shipping?</h4>
                <p className="text-gray-600">Use penny sleeves and top loaders or card savers for each card. Place them in a sturdy box with padding to prevent movement during transit. Include your packing slip inside the package.</p>
              </div>
              
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <h4 className="font-semibold text-gray-900 mb-2">When am I charged for my submission?</h4>
                <p className="text-gray-600">Your payment method is saved when you submit, but you're not charged until we receive your cards at our grading lab. This ensures you only pay for cards that arrive safely.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-[#002554]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-gray-200 mb-8 max-w-2xl mx-auto">
              Join thousands of collectors who trust NICE Grading to protect and certify their valuable cards.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/new-submission">
                <Button className="bg-white text-[#003594] hover:bg-gray-100 px-8 py-6 text-base font-semibold w-full sm:w-auto">
                  Start Your Submission
                </Button>
              </Link>
              <Link to="/grading-standards">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-base font-semibold w-full sm:w-auto">
                  View Grading Standards
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HowItWorksPage;
